import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import NavbarMenuTsoPq from '@components/Menu/NavbarMenuTsoPq';
import { NavigationNodeProps } from '@custom-types/NavbarTypes';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, ButtonProps, ClickAwayListener, ListItem, Popper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

type MenuProps = {
  id: number;
  options: NavigationNodeProps[] | undefined;
  label: string;
  path: string;
  active: boolean;
};

interface StyledNavigationNodeProps extends ButtonProps {
  active?: boolean;
}

const StyledNavigationNode = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})<StyledNavigationNodeProps>(({ active }) => {
  return {
    '&.MuiButtonBase-root': {
      fontFamily: tsoPqTheme.typography.openSans,
      padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_8,
      color: active
        ? tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_hover
        : tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
      background: active ? tsoPqTheme.palette.background.primary.color_primary_background_bg_prim : 'transparent',
      height: '2.625rem',
      margin: '0rem',
      display: 'flex',
      borderRadius: 0,
      outline: 'none',
      textTransform: 'capitalize',

      '&:hover': {
        color: active
          ? tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_pressed
          : tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_hover,
        background: active
          ? tsoPqTheme.palette.background.primary.color_primary_background_bg_prim
          : tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_accent,
        textDecoration: 'underline',
      },
      '&:active': {
        color: active
          ? tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_pressed
          : tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_hover,
        background: active
          ? tsoPqTheme.palette.background.primary.color_primary_background_bg_prim
          : tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_pressed_lt,
      },
    },
  };
});

const StyledMenuItem = styled(ListItem)(() => {
  return {
    minWidth: '17.5rem',
    minHeight: '1.875rem',
    padding: '0.25rem 0.5rem 0.25rem 1.5rem',

    '&:hover': { background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim },
    '&:active': {
      background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_pressed_lt,
    },
  };
});

const NavigationNodeTsoPq = ({ id, options, label, path, active }: MenuProps) => {
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleHover = (event: React.MouseEvent<HTMLElement>, label: string) => {
    setAnchorElNav(event.currentTarget);
    setHoveredMenu(label);
  };

  const handleClose = () => {
    setAnchorElNav(null);
    setHoveredMenu(null);
  };

  const handleClick = (event: React.MouseEvent, path?: string) => {
    event.stopPropagation();
    handleClose();
    if (path) {
      navigate(path);
    }
  };

  return (
    <Box
      key={id}
      onMouseEnter={(event) => options && handleHover(event, label)}
      onMouseLeave={handleClose}
      sx={{ position: 'relative' }}
    >
      <StyledNavigationNode
        data-testid="navigationNode"
        onClick={(event) => handleClick(event, path)}
        endIcon={options && <KeyboardArrowDownIcon />}
        disableRipple
        active={active}
      >
        {t(label)}
        <Popper
          data-testid="navigationNodeMenu"
          open={hoveredMenu === label}
          anchorEl={anchorElNav}
          placement="bottom-start"
          disablePortal
        >
          <ClickAwayListener onClickAway={handleClose}>
            <NavbarMenuTsoPq onMouseLeave={handleClose}>
              {options?.map((menuItem: NavigationNodeProps) => (
                <StyledMenuItem
                  key={menuItem.id}
                  onClick={(event) => {
                    handleClick(event, menuItem.path);
                  }}
                >
                  <Typography>{t(menuItem.label)}</Typography>
                </StyledMenuItem>
              ))}
            </NavbarMenuTsoPq>
          </ClickAwayListener>
        </Popper>
      </StyledNavigationNode>
    </Box>
  );
};

export default NavigationNodeTsoPq;
